import {
  Edit2,
  EllipsisVerticalIcon,
  FolderKanbanIcon,
  LinkIcon,
  MegaphoneIcon,
  MessageCircle,
  TrashIcon,
  UsersIcon,
} from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  PermissionGuard,
} from "@/components";
import { FloatingLabelTextarea } from "@/components/ui/floating-label-textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { useI18n } from "@/context";
import { usePermissions } from "@/context/permissions";
import { formatCpfCnpj } from "@/core/utils";
import { useUsers } from "@/pages/users/users.hooks";

import { useClients } from "../clients.hooks";
import { TRejectionReason } from "../clients.schema";
import {
  ClientStatus,
  EBusinessClassification,
  ETypeOfPartner,
  TClientExtendedResponse,
} from "../clients.types";
import { PartnerPortalCard } from "./partner-portal-card";

export const ClientReviewData: React.FC<{
  client: TClientExtendedResponse;
  setIsClientReviewOpen: (isClientReviewOpen: boolean) => void;
  openClientConfigProducts?: (open: boolean) => void;
  setSelectedClient?: (client: TClientExtendedResponse) => void;
  deleteClient?: (id: string) => void;
  selectClient?: (client: TClientExtendedResponse) => void;
}> = ({ 
  client, 
  setIsClientReviewOpen,
  deleteClient,
  selectClient,
}) => {
  const { t } = useI18n();
  const { toast: toastHook } = useToast();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canDeletePartner = hasPermission("partners:delete");
  const canCopyRegistrationLink = hasPermission("partners:view");
  const showMoreActionsMenu =
    (client.status === ClientStatus.pending_data && canCopyRegistrationLink) ||
    (deleteClient != null && canDeletePartner);
  const { isLoading, rejectionForm, rejectClientData, approveClientData, attachUsersToOrganization } =
    useClients();
  
  const { users = [] } = useUsers();

  const [isRejectionModalOpen, setIsRejectionModalOpen] = React.useState(false);
  
  // Estados para a modal de vincular usuários
  const [usersModalOpen, setUsersModalOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedUsers, setSelectedUsers] = React.useState<string[]>([]);

  // Função para abrir modal de vincular usuários
  const handleOpenUsersModal = () => {
    setUsersModalOpen(true);
    setSearchTerm("");
    setSelectedUsers([]);
  };

  // Função para vincular usuários
  const handleLinkUsers = () => {
    if (selectedUsers.length > 0) {
      attachUsersToOrganization(selectedUsers);
      toastHook({
        title: "Usuários vinculados com sucesso!",
        description: `${selectedUsers.length} usuário(s) vinculado(s) ao cliente.`,
      });
      setUsersModalOpen(false);
    } else {
      toastHook({
        title: "Nenhum usuário selecionado",
        description: "Selecione pelo menos um usuário para vincular.",
        variant: "destructive",
      });
    }
  };

  // Filtrar usuários baseado no termo de busca
  const filteredUsers = users.filter(
    (user) =>
      user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleReject = async (data: TRejectionReason) => {
    try {
      toastHook({
        title: t("generic.success"),
        description: t("clients.success.partnerRejected"),
      });
      rejectClientData({
        client_id: client.id,
        rejection_reason: data.rejection_reason,
      });
      setIsRejectionModalOpen(false);
      rejectionForm.reset();
    } catch (error) {
      toastHook({
        title: t("generic.error"),
        description: t("clients.errors.rejectFailed"),
      });
    }
  };

  const getTypePartnerText = (typePartner?: ETypeOfPartner) => {
    if (!typePartner) {
      return "Sem tipo";
    }
    if (typePartner === ETypeOfPartner.client) {
      return t("clients.form.fields.type.partner.client");
    }
    // Para business_indicator, retorna apenas "Hunter"
    return "Hunter";
  };

  const getTypePartnerBadgeVariant = (typePartner?: ETypeOfPartner) => {
    switch (typePartner) {
      case ETypeOfPartner.client:
        return "default";
      case ETypeOfPartner.business_indicator:
        return "secondary";
      default:
        return "outline";
    }
  };

  const getBusinessClassificationText = (
    businessClassification?: EBusinessClassification
  ) => {
    switch (businessClassification) {
      case EBusinessClassification.servicos:
        return t("clients.business_classification.servicos");
    }
  };

  return (
    <>
      {!client && (
        <div className="container mx-auto py-6 space-y-6">
          <p className="text-muted-foreground">Cliente não encontrado</p>
        </div>
      )}

      {client && (
        <div className="container mx-auto py-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">
                {t("clients.review.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("clients.review.description")}
              </p>
            </div>
          </div>

          {/* Ações do Parceiro */}
          <Card>
            <CardHeader>
              <CardTitle>{t("generic.actions")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {selectClient && (
                  <PermissionGuard permission="partners:edit">
                    <Button
                      type="button"
                      onClick={() => selectClient(client)}
                      size={"sm"}
                      variant={"outline"}
                    >
                      <Edit2 className="h-4 w-4 mr-2" />
                      {t("generic.edit")}
                    </Button>
                  </PermissionGuard>
                )}
                <PermissionGuard permission="partners:view">
                  <Button
                    onClick={handleOpenUsersModal}
                    size={"sm"}
                    variant={"outline"}
                  >
                    <UsersIcon className="h-4 w-4 mr-2" />
                    {t("dashboard.clients.sub.linkUsers")}
                  </Button>
                </PermissionGuard>
                <Button
                  onClick={() =>
                    navigate(`/clients/${client.id}/projects/list`)
                  }
                  size={"sm"}
                  variant={"outline"}
                  disabled={client.status !== ClientStatus.approved}
                >
                  <FolderKanbanIcon className="h-4 w-4 mr-2" />
                  Ver Projetos
                </Button>
                <Button
                  onClick={() => navigate(`/clients/${client.id}/sub-partners`)}
                  size={"sm"}
                  variant={"outline"}
                >
                  <UsersIcon className="h-4 w-4 mr-2" />
                  Listar Parceiros
                </Button>
                <Button
                  onClick={() => navigate(`/clients/${client.id}/campaigns`)}
                  size={"sm"}
                  variant={"outline"}
                  disabled={client.status !== ClientStatus.approved}
                >
                  <MegaphoneIcon className="h-4 w-4 mr-2" />
                  {t("campaigns.actions.viewCampaigns")}
                </Button>
                <PermissionGuard permission="whatsapp:view">
                  <Button
                    onClick={() =>
                      navigate(`/clients/${client.id}/whatsapp`)
                    }
                    size={"sm"}
                    variant={"outline"}
                    disabled={client.status !== ClientStatus.approved}
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    {t("partnerWhatsappInbox.actions.openInbox")}
                  </Button>
                </PermissionGuard>
                <PermissionGuard permission="whatsapp:view">
                  <Button
                    onClick={() =>
                      navigate(`/clients/${client.id}/whatsapp-meta`)
                    }
                    size={"sm"}
                    variant={"outline"}
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    {t("partnerWhatsappInbox.actions.metaConfig")}
                  </Button>
                </PermissionGuard>
                {showMoreActionsMenu && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size={"sm"} variant={"outline"}>
                      <EllipsisVerticalIcon className="h-4 w-4 mr-2" />
                      Mais ações
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {client.status === ClientStatus.pending_data &&
                      canCopyRegistrationLink && (
                      <DropdownMenuItem
                        onSelect={() => {
                          navigator.clipboard.writeText(
                            `${
                              import.meta.env.VITE_PANEL_BASE_URL
                            }/cadastro-novo-cliente/${client.id}`
                          );
                          toastHook({
                            title: t("generic.link_copied"),
                          });
                        }}
                      >
                        <LinkIcon className="h-4 w-4 mr-2" />
                        Copiar link de cadastro
                      </DropdownMenuItem>
                    )}
                    {deleteClient && canDeletePartner && (
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive focus:bg-destructive/10"
                        onSelect={() => deleteClient(client.id)}
                      >
                        <TrashIcon className="h-4 w-4 mr-2" />
                        {t("generic.delete")}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                )}
              </div>
            </CardContent>
          </Card>

          <PartnerPortalCard organizationId={client.id} />

          <Separator />

          {/* Informações Básicas */}
          <Card>
            <CardHeader>
              <CardTitle>{t("clients.review.basicInfo")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t("clients.form.fields.name")}
                  </label>
                  <p className="text-lg">{client.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t("clients.form.fields.corporate_name")}
                  </label>
                  <p className="text-lg">{client.corporate_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t("clients.form.fields.cnpj")}
                  </label>
                  <p className="text-lg">{formatCpfCnpj(client.cnpj)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Email
                  </label>
                  <p className="text-lg">
                    {client.email || t("clients.review.notInformed")}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Classificação Empresarial
                  </label>
                  <p className="text-lg">
                    {getBusinessClassificationText(
                      client.business_classification
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Endereço */}
          {client.address && (
            <Card>
              <CardHeader>
                <CardTitle>{t("clients.review.address")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      CEP
                    </label>
                    <p className="text-lg">{client.address.postal_code}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Rua
                    </label>
                    <p className="text-lg">{client.address.street}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Número
                    </label>
                    <p className="text-lg">{client.address.number}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Complemento
                    </label>
                    <p className="text-lg">
                      {client.address.complement ||
                        t("clients.review.notInformed")}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Bairro
                    </label>
                    <p className="text-lg">{client.address.neighborhood}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Cidade
                    </label>
                    <p className="text-lg">{client.address.city}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Estado
                    </label>
                    <p className="text-lg">{client.address.state}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      País
                    </label>
                    <p className="text-lg">{client.address.country}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Representante da Empresa */}
          {client.company_representative && (
            <Card>
              <CardHeader>
                <CardTitle>{t("clients.review.representative")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Nome
                    </label>
                    <p className="text-lg">
                      {client.company_representative?.first_name || ""}{" "}
                      {client.company_representative?.last_name || ""}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Email
                    </label>
                    <p className="text-lg">
                      {client.company_representative?.personal_email || "-"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      CPF
                    </label>
                    <p className="text-lg">
                      {formatCpfCnpj(client.company_representative?.cpf)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Telefone
                    </label>
                    <p className="text-lg">
                      {client.company_representative?.mobile_phone || "-"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Conta Bancária */}
          {client.bank_account && (
            <Card>
              <CardHeader>
                <CardTitle>{t("clients.review.bankAccount")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Banco
                    </label>
                    <p className="text-lg">{client.bank_account.bank_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Agência
                    </label>
                    <p className="text-lg">{client.bank_account.bank_branch}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Conta Corrente
                    </label>
                    <p className="text-lg">
                      {client.bank_account.checking_account}-
                      {client.bank_account.checking_account_digit}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t("clients.review.pixKey")}
                    </label>
                    <p className="text-lg">
                      {client.bank_account.pix_key?.trim() || "-"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Documentos */}
          {client.document && (
            <Card>
              <CardHeader>
                <CardTitle>{t("clients.review.documents")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Logo da Empresa
                    </label>
                    <p className="text-lg flex items-center gap-2">
                      {client.document.company_logo ? (
                        <>
                          {t("clients.review.sent")}
                          {client.document.company_logo && (
                            <a
                              href={client.document.company_logo}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-2 text-primary underline text-sm"
                            >
                              {t("clients.review.openDocument")}
                            </a>
                          )}
                        </>
                      ) : (
                        t("clients.review.notSent")
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Cartão CNPJ
                    </label>
                    <p className="text-lg flex items-center gap-2">
                      {client.document.cnpj_card ? (
                        <>
                          {t("clients.review.sent")}
                          {client.document.cnpj_card && (
                            <a
                              href={client.document.cnpj_card}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-2 text-primary underline text-sm"
                            >
                              {t("clients.review.openDocument")}
                            </a>
                          )}
                        </>
                      ) : (
                        t("clients.review.notSent")
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Contrato Assinado entre as Partes
                    </label>
                    <p className="text-lg flex items-center gap-2">
                      {client.document.signed_contract ? (
                        <>
                          {t("clients.review.sent")}
                          {Array.isArray(client.document.signed_contract)
                            ? client.document.signed_contract.map(
                                (contractUrl, idx) => (
                                  <a
                                    key={idx}
                                    href={contractUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ml-2 text-primary underline text-sm"
                                  >
                                    {t("clients.review.openDocument")}{" "}
                                    {client.document?.signed_contract &&
                                    client.document.signed_contract.length > 1
                                      ? `(${idx + 1})`
                                      : ""}
                                  </a>
                                )
                              )
                            : client.document.signed_contract && (
                                <a
                                  href={client.document.signed_contract}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ml-2 text-primary underline text-sm"
                                >
                                  {t("clients.review.openDocument")}
                                </a>
                              )}
                        </>
                      ) : (
                        t("clients.review.notSent")
                      )}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}


          <Separator />

          <div className="flex justify-end space-x-4">
            {client.status !== ClientStatus.approved && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setIsRejectionModalOpen(true)}
                  disabled={isLoading}
                >
                  {t("clients.review.actions.reject")}
                </Button>
                <Button
                  onClick={() => {
                    approveClientData(client.id);
                  }}
                  disabled={isLoading}
                >
                  {t("clients.review.actions.approve")}
                </Button>
              </>
            )}
            <Button
              onClick={() => {
                setIsClientReviewOpen(false);
              }}
              disabled={isLoading}
            >
              {t("generic.close")}
            </Button>
          </div>

          {/* Modal de Rejeição */}
          <Dialog
            open={isRejectionModalOpen}
            onOpenChange={setIsRejectionModalOpen}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {t("clients.review.rejectionModal.title")}
                </DialogTitle>
              </DialogHeader>
              <Form {...rejectionForm}>
                <form
                  onSubmit={rejectionForm.handleSubmit(handleReject)}
                  className="space-y-4"
                >
                  <FormField
                    control={rejectionForm.control}
                    name="rejection_reason"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelTextarea
                            label={t("clients.review.rejectionModal.reason")}
                            placeholder={t(
                              "clients.review.rejectionModal.placeholder"
                            )}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex justify-end space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsRejectionModalOpen(false);
                        rejectionForm.reset();
                      }}
                    >
                      {t("clients.review.actions.cancel")}
                    </Button>
                    <Button type="submit" disabled={isLoading}>
                      {t("clients.review.rejectionModal.confirm")}
                    </Button>
                  </div>
                </form>
              </Form>
            </DialogContent>
          </Dialog>

          {/* Modal para vincular usuários */}
          <Dialog open={usersModalOpen} onOpenChange={setUsersModalOpen}>
            <DialogContent className="w-[80vw] max-w-none max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Vincular Usuários ao Cliente</DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                {/* Busca de usuários */}
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <Label>Buscar usuários</Label>
                    <Input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Digite nome ou email do usuário..."
                    />
                  </div>
                  <Button
                    onClick={handleLinkUsers}
                    disabled={selectedUsers.length === 0}
                  >
                    Vincular{" "}
                    {selectedUsers.length > 0 ? `(${selectedUsers.length})` : ""}
                  </Button>
                </div>

                {/* Tabela de usuários */}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Selecionar</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Email</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          className="text-center text-gray-500 py-8"
                        >
                          {searchTerm
                            ? "Nenhum usuário encontrado."
                            : "Nenhum usuário disponível."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedUsers.includes(user.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedUsers((prev) => [...prev, user.id]);
                                } else {
                                  setSelectedUsers((prev) =>
                                    prev.filter((id) => id !== user.id)
                                  );
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {user.first_name} {user.last_name}
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </>
  );
};
